import { Injectable } from '@angular/core';
import { PermissionService } from 'app/core/auth/permission.service';
import { Permissions } from 'app/core/auth/permissions.constants';
import { GlobalSearchItemResult, GlobalSearchItemResultPagedLookupResult } from 'kiota/generated/models';
import { ApiOrchestrator } from 'kiota/services/api.orchestrator';
import { from, map, Observable, of } from 'rxjs';

/**
 * Kind of row the dropdown shows: an approved PO has its own tracking page, a PO still waiting
 * for approval lives only as a card on the dashboard, so the two navigate elsewhere.
 */
export type GlobalSearchResultKind = 'purchaseOrder' | 'pendingConfirmation';

export interface GlobalSearchResult extends GlobalSearchItemResult {
    kind: GlobalSearchResultKind;
}

/** One page of one of the two sources: `hasMore` is what keeps the infinite scroll going */
export interface GlobalSearchPage {
    items: GlobalSearchResult[];
    hasMore: boolean;
    totalCount: number;
}

const EMPTY_PAGE: GlobalSearchPage = { items: [], hasMore: false, totalCount: 0 };

@Injectable({
    providedIn: 'root',
})
export class GlobalSearchService {
    constructor(
        private apiOrchestrator: ApiOrchestrator,
        private permissionService: PermissionService
    ) {}

    /**
     * The two sources are paged independently — they answer to two different permissions and a
     * user may hold only one of them, so a source the user cannot read resolves to an empty page
     * instead of an error.
     */
    searchPurchaseOrders(term: string, skip: number, take: number): Observable<GlobalSearchPage> {
        if (!this.permissionService.canView(Permissions.ShipmentTrackingView)) {
            return of(EMPTY_PAGE);
        }

        return from(
            this.apiOrchestrator.client.api.v1.purchaseOrders.search.get({ queryParameters: { searchTerm: term, skip, take } })
        ).pipe(map((result) => this.toPage(result, 'purchaseOrder')));
    }

    searchPendingConfirmations(term: string, skip: number, take: number): Observable<GlobalSearchPage> {
        if (!this.permissionService.canView(Permissions.DashboardPendingConfirmationsView)) {
            return of(EMPTY_PAGE);
        }

        return from(
            this.apiOrchestrator.client.api.v1.pendingConfirmations.search.get({ queryParameters: { searchTerm: term, skip, take } })
        ).pipe(map((result) => this.toPage(result, 'pendingConfirmation')));
    }

    get canSearchPurchaseOrders(): boolean {
        return this.permissionService.canView(Permissions.ShipmentTrackingView);
    }

    get canSearchPendingConfirmations(): boolean {
        return this.permissionService.canView(Permissions.DashboardPendingConfirmationsView);
    }

    get canSearch(): boolean {
        return this.canSearchPurchaseOrders || this.canSearchPendingConfirmations;
    }

    private toPage(result: GlobalSearchItemResultPagedLookupResult | undefined, kind: GlobalSearchResultKind): GlobalSearchPage {
        return {
            items: (result?.items ?? []).map((item) => ({ ...item, kind })),
            hasMore: result?.hasMore ?? false,
            totalCount: result?.totalCount ?? 0,
        };
    }
}
